import { z } from 'zod';
import { nonNegativePaiseSchema } from '../money/paise.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';

/**
 * COUPONS — the marketing lever this product did not have.
 *
 * A shop already has a discount field: type "500" into the booking form and the bill comes
 * down. That is not the same thing, and the difference is the whole reason this exists. A
 * discount is a decision one person makes at one counter and nobody can count afterwards. A
 * coupon is a decision the OWNER makes once — "20% off for Deepavali, up to ₹500, on bills over
 * ₹2,000, for the first hundred customers" — which the counter then merely applies, and which
 * can be turned off, counted, and argued about with evidence.
 *
 * Four decisions shape this file.
 *
 * **The code is the identity, and it is case-insensitive.** Somebody reads "DEEPAVALI20" off a
 * printed slip in a shop; making them match the shop's own capitalisation would be a support
 * call a week. It is stored as the owner typed it and matched folded.
 *
 * **The discount is FROZEN at the moment it is applied.** A percentage is turned into rupees
 * against the bill as it was quoted, and that figure rides on the order. The same reasoning as
 * the add-on snapshot: re-pricing later must never silently re-price a bill somebody agreed to.
 * If the bill later shrinks below the discount, `orderTotals` clamps — a coupon can zero a bill
 * and can never make one owe the customer money.
 *
 * **Usage is counted from redemptions, never from a field somebody increments.** "First hundred
 * customers" is a promise that has to survive two counters booking at once, and a counter on
 * the coupon document would be a lost update the first busy Saturday. Every application writes a
 * redemption row; every limit is a count over those rows.
 *
 * **Nothing here ever deletes a redemption.** Cancelling an order RELEASES it — the row stays,
 * marked, so "we gave away 40 of these and 3 were cancelled" is answerable, which "we gave away
 * 37" is not.
 */

/* -------------------------------------------------------------------------------------------
 * The code
 * ---------------------------------------------------------------------------------------- */

export const COUPON_CODE_MAX_LENGTH = 24;

/**
 * Letters, digits, hyphens. No spaces and no punctuation, because this is read off paper and
 * typed on a phone — and a code containing a character somebody cannot find on a keyboard is a
 * code that gets typed wrong and blamed on the app.
 */
export const couponCodeSchema = z
  .string()
  .trim()
  .min(3, 'A code is at least 3 characters')
  .max(COUPON_CODE_MAX_LENGTH)
  .regex(/^[A-Za-z0-9-]+$/, 'Use letters, numbers and hyphens only');

/** How a code is compared. Case is the shop's to choose and never the customer's to remember. */
export function normaliseCouponCode(code: string): string {
  return code.trim().toUpperCase();
}

/* -------------------------------------------------------------------------------------------
 * What it takes off
 * ---------------------------------------------------------------------------------------- */

/**
 * `percent` takes a share of the bill; `fixed` takes a number of rupees.
 *
 * A percentage needs a CAP to be safe to hand out — "20% off" on a ₹40,000 bridal order is
 * ₹8,000 nobody meant to give away — which is why `maxDiscountPaise` exists and why the UI
 * should nudge towards setting one.
 */
export const COUPON_TYPES = ['percent', 'fixed'] as const;
export type CouponType = (typeof COUPON_TYPES)[number];
export const couponTypeSchema = z.enum(COUPON_TYPES);

const couponFieldsSchema = z.object({
  code: couponCodeSchema,
  /** What the shop calls it internally — "Deepavali 2026". Never shown to the customer. */
  description: z.string().trim().max(200).nullish(),
  type: couponTypeSchema,
  /**
   * For `percent`, a whole percentage from 1 to 100. For `fixed`, an amount in paise. One
   * field rather than two because a coupon is one or the other and a shape with two nullable
   * amounts invites a row that is somehow both.
   */
  value: z.number().int().positive(),
  /** Below this the code is refused. Zero — the default — means any bill. */
  minOrderPaise: nonNegativePaiseSchema.default(0),
  /** The ceiling on a percentage. `null` is uncapped, which for a percentage is a risk. */
  maxDiscountPaise: nonNegativePaiseSchema.positive('A cap must be more than zero').nullish(),
  /** When the code starts working. Absent means immediately. */
  validFrom: dateInputSchema.nullish(),
  /** The last instant it works. Absent means it never expires on its own. */
  validTo: dateInputSchema.nullish(),
  /** How many times it may be redeemed across the whole shop. `null` is unlimited. */
  usageLimit: z.number().int().positive().nullish(),
  /** How many times ONE customer may use it. `null` is unlimited. */
  perCustomerLimit: z.number().int().positive().nullish(),
  /** Off switch. A coupon is switched off, never deleted, so its redemptions stay readable. */
  active: z.boolean().default(true),
});

/** A percentage above 100 is a mistake, and a capped `fixed` amount is a contradiction. */
function couponShapeIsCoherent(value: {
  type: CouponType;
  value: number;
  maxDiscountPaise?: number | null | undefined;
}): boolean {
  if (value.type === 'percent') return value.value <= 100;
  return true;
}

export const PERCENT_OVER_HUNDRED = {
  message: 'A percentage cannot be more than 100',
  path: ['value'] as const,
};

export const createCouponSchema = couponFieldsSchema
  .refine(couponShapeIsCoherent, {
    message: PERCENT_OVER_HUNDRED.message,
    path: [...PERCENT_OVER_HUNDRED.path],
  })
  .refine(
    (value) =>
      !value.validFrom || !value.validTo || value.validTo.getTime() >= value.validFrom.getTime(),
    { message: 'The end date must not be before the start date', path: ['validTo'] },
  );
export type CreateCouponInput = z.input<typeof createCouponSchema>;

/**
 * `code` and `type` are absent: what a coupon IS does not change once it has been printed on
 * something. Switch it off and make a new one.
 */
export const updateCouponSchema = couponFieldsSchema
  .omit({ code: true, type: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateCouponInput = z.input<typeof updateCouponSchema>;

export const couponSchema = z.object({
  id: objectIdSchema,
  code: z.string(),
  description: z.string().nullable(),
  type: couponTypeSchema,
  value: z.number().int(),
  minOrderPaise: nonNegativePaiseSchema,
  maxDiscountPaise: nonNegativePaiseSchema.nullable(),
  validFrom: isoDateStringSchema.nullable(),
  validTo: isoDateStringSchema.nullable(),
  usageLimit: z.number().int().nullable(),
  perCustomerLimit: z.number().int().nullable(),
  active: z.boolean(),
  /** Counted from redemptions, never from a field somebody increments. */
  timesRedeemed: z.number().int().nonnegative(),
  /** What those redemptions have cost the shop, in paise. The question a lever is judged on. */
  discountGivenPaise: nonNegativePaiseSchema,
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Coupon = z.infer<typeof couponSchema>;

export const couponSearchQuerySchema = paginationQuerySchema.extend({
  /** Only the ones a counter could actually use today. */
  usableOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
  q: z.string().trim().max(60).optional(),
});
export type CouponSearchQuery = z.infer<typeof couponSearchQuerySchema>;

export const couponPageSchema = z.object({
  items: z.array(couponSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type CouponPage = z.infer<typeof couponPageSchema>;

/* -------------------------------------------------------------------------------------------
 * Applying one
 * ---------------------------------------------------------------------------------------- */

/**
 * Why a code did not work.
 *
 * Every one of these is a DIFFERENT sentence at the counter, which is why they are separate
 * values rather than one `invalid`. "That code has expired" sends somebody to ask for another;
 * "that is for bills over ₹2,000" tells them to add a second garment. A single refusal would
 * turn both into an argument with the person at the counter.
 */
export const COUPON_REFUSALS = [
  'unknown',
  'inactive',
  'not_started',
  'expired',
  'below_minimum',
  'usage_limit_reached',
  'customer_limit_reached',
] as const;
export type CouponRefusal = (typeof COUPON_REFUSALS)[number];
export const couponRefusalSchema = z.enum(COUPON_REFUSALS);

/** What the booking form asks before it shows a figure. */
export const previewCouponSchema = z.object({
  code: couponCodeSchema,
  /** The bill as it stands — the garments, before delivery and before any manual discount. */
  subtotalPaise: nonNegativePaiseSchema,
  /** Whose order, for the per-customer limit. Absent skips only that check. */
  customerId: objectIdSchema.nullish(),
});
export type PreviewCouponInput = z.input<typeof previewCouponSchema>;

export const couponPreviewSchema = z.object({
  code: z.string(),
  /** True when it would apply to this bill right now. */
  usable: z.boolean(),
  /** What it would take off, in paise. Zero when it would not apply. */
  discountPaise: nonNegativePaiseSchema,
  /** Named so the client can write the right sentence. `null` when it IS usable. */
  refusal: couponRefusalSchema.nullable(),
  /** The bill this coupon needs, when `below_minimum` is why it was refused. */
  minOrderPaise: nonNegativePaiseSchema.nullable(),
  description: z.string().nullable(),
});
export type CouponPreview = z.infer<typeof couponPreviewSchema>;

/**
 * What one coupon takes off one bill, in paise.
 *
 * Pure, and shared, so the figure the booking form shows before saving and the figure the
 * server freezes onto the order are computed by the same lines. A percentage is rounded to
 * whole paise; the cap is applied after the rounding, and the result can never exceed the
 * subtotal — a coupon may zero a bill and may never make one owe the customer money.
 */
export function couponDiscountPaise(
  coupon: Pick<Coupon, 'type' | 'value' | 'maxDiscountPaise'>,
  subtotalPaise: number,
): number {
  const subtotal = Math.max(0, Math.round(subtotalPaise));
  const raw =
    coupon.type === 'percent'
      ? Math.round((subtotal * coupon.value) / 100)
      : Math.round(coupon.value);
  const capped = coupon.maxDiscountPaise === null ? raw : Math.min(raw, coupon.maxDiscountPaise);
  return Math.max(0, Math.min(capped, subtotal));
}

/**
 * Whether a coupon may be used on this bill, and why not.
 *
 * Takes the counts rather than reading them, so both runtimes can call it: the browser has
 * neither a database nor any business knowing how redemptions are stored.
 */
export interface CouponUsageFacts {
  readonly timesRedeemed: number;
  /** How many times THIS customer has redeemed it. Pass 0 when there is no customer yet. */
  readonly customerRedemptions: number;
}

export function couponRefusalFor(
  coupon: Pick<
    Coupon,
    'active' | 'validFrom' | 'validTo' | 'minOrderPaise' | 'usageLimit' | 'perCustomerLimit'
  >,
  subtotalPaise: number,
  usage: CouponUsageFacts,
  now: Date = new Date(),
): CouponRefusal | null {
  if (!coupon.active) return 'inactive';
  if (coupon.validFrom && new Date(coupon.validFrom).getTime() > now.getTime()) {
    return 'not_started';
  }
  if (coupon.validTo && new Date(coupon.validTo).getTime() < now.getTime()) return 'expired';
  if (subtotalPaise < coupon.minOrderPaise) return 'below_minimum';
  if (coupon.usageLimit !== null && usage.timesRedeemed >= coupon.usageLimit) {
    return 'usage_limit_reached';
  }
  if (coupon.perCustomerLimit !== null && usage.customerRedemptions >= coupon.perCustomerLimit) {
    return 'customer_limit_reached';
  }
  return null;
}

/* -------------------------------------------------------------------------------------------
 * The redemption
 * ---------------------------------------------------------------------------------------- */

/** `released` is a redemption that was undone by cancelling the order. The row stays. */
export const COUPON_REDEMPTION_STATUSES = ['applied', 'released'] as const;
export type CouponRedemptionStatus = (typeof COUPON_REDEMPTION_STATUSES)[number];
export const couponRedemptionStatusSchema = z.enum(COUPON_REDEMPTION_STATUSES);

export const couponRedemptionSchema = z.object({
  id: objectIdSchema,
  couponId: objectIdSchema,
  code: z.string(),
  orderId: objectIdSchema,
  orderNumber: z.string(),
  customerId: objectIdSchema,
  customerName: z.string(),
  discountPaise: nonNegativePaiseSchema,
  status: couponRedemptionStatusSchema,
  redeemedAt: isoDateStringSchema,
  releasedAt: isoDateStringSchema.nullable(),
});
export type CouponRedemption = z.infer<typeof couponRedemptionSchema>;

export const couponRedemptionPageSchema = z.object({
  items: z.array(couponRedemptionSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type CouponRedemptionPage = z.infer<typeof couponRedemptionPageSchema>;

/** Putting a code on an order that already exists, or taking it off. */
export const applyCouponSchema = z.object({ code: couponCodeSchema });
export type ApplyCouponInput = z.input<typeof applyCouponSchema>;
